export const SECURITY_COURSES = [
  {
    id: 'cybersecurity-fundamentals',
    title: 'Cybersecurity Fundamentals',
    description: 'Master essential cybersecurity concepts, threat analysis, and defense strategies for modern applications.',
    level: 'beginner',
    totalDuration: '10-12 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic networking knowledge', 'Understanding of web technologies'],
    learningOutcomes: [
      'Understand common security threats and vulnerabilities',
      'Implement secure coding practices',
      'Perform basic penetration testing',
      'Design secure system architectures',
      'Apply encryption and authentication mechanisms',
      'Respond to security incidents effectively'
    ],
    modules: [
      {
        id: 'security-foundations',
        title: 'Security Foundations',
        description: 'Core cybersecurity concepts, threat landscape, and security principles',
        duration: '90 minutes',
        difficulty: 'beginner',
        objectives: [
          'Understand the CIA triad (Confidentiality, Integrity, Availability)',
          'Identify common attack vectors and threats',
          'Learn security frameworks and compliance standards',
          'Implement defense-in-depth strategies'
        ],
        content: `# Cybersecurity Foundations

## The CIA Triad

### Core Security Principles
- **Confidentiality**: Information is accessible only to authorized users
- **Integrity**: Data remains accurate and unaltered
- **Availability**: Systems and data are accessible when needed

## Common Threat Landscape

### OWASP Top 10 Security Risks
1. **Injection Attacks**: SQL, NoSQL, LDAP injection
2. **Broken Authentication**: Session management flaws
3. **Sensitive Data Exposure**: Inadequate data protection
4. **XML External Entities (XXE)**: XML parser vulnerabilities
5. **Broken Access Control**: Authorization failures
6. **Security Misconfiguration**: Default/incomplete configurations
7. **Cross-Site Scripting (XSS)**: Input validation failures
8. **Insecure Deserialization**: Object injection vulnerabilities
9. **Known Vulnerabilities**: Outdated components
10. **Insufficient Logging**: Inadequate monitoring

## Security Frameworks

### NIST Cybersecurity Framework
\`\`\`
1. Identify: Asset management, risk assessment
2. Protect: Access controls, data security
3. Detect: Monitoring, anomaly detection
4. Respond: Incident response planning
5. Recover: Business continuity, disaster recovery
\`\`\`

### ISO 27001 Controls
- Information security policies
- Risk management procedures
- Asset management controls
- Access control mechanisms
- Cryptography standards
- Physical and environmental security

## Secure Coding Practices

### Input Validation
\`\`\`javascript
// Vulnerable code
function getUserData(userId) {
    const query = \`SELECT * FROM users WHERE id = \${userId}\`;
    return database.query(query);
}

// Secure code
function getUserData(userId) {
    // Input validation
    if (!userId || typeof userId !== 'string') {
        throw new Error('Invalid user ID');
    }
    
    // Parameterized query
    const query = 'SELECT * FROM users WHERE id = ?';
    return database.query(query, [userId]);
}
\`\`\`

### Authentication Security
\`\`\`javascript
// Secure password hashing
const bcrypt = require('bcrypt');

async function hashPassword(password) {
    const saltRounds = 12;
    return await bcrypt.hash(password, saltRounds);
}

async function verifyPassword(password, hash) {
    return await bcrypt.compare(password, hash);
}

// JWT token security
const jwt = require('jsonwebtoken');

function generateToken(user) {
    return jwt.sign(
        { userId: user.id, role: user.role },
        process.env.JWT_SECRET,
        { 
            expiresIn: '1h',
            issuer: 'your-app',
            audience: 'your-users'
        }
    );
}
\`\`\``,
        practice: {
          task: 'Implement a secure user authentication system with proper input validation and password hashing',
          starter: `// Implement secure authentication
const express = require('express');
const app = express();

// Add your secure authentication code here`,
          hints: [
            'Use bcrypt for password hashing with appropriate salt rounds',
            'Implement input validation for all user inputs',
            'Use JWT tokens with proper expiration and secrets',
            'Add rate limiting for login attempts',
            'Implement secure session management'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'security-1',
              question: 'Which component of the CIA triad ensures data is not modified by unauthorized users?',
              options: ['Confidentiality', 'Integrity', 'Availability', 'Authentication'],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'Integrity ensures that data remains accurate and unaltered by unauthorized parties.',
              points: 10
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'security'
  },
  {
    id: 'web-application-security',
    title: 'Web Application Security',
    description: 'Advanced web application security testing, OWASP compliance, and secure development practices.',
    level: 'intermediate',
    totalDuration: '8-10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Web development experience', 'Basic security knowledge', 'Understanding of HTTP/HTTPS'],
    learningOutcomes: [
      'Perform comprehensive security testing',
      'Implement OWASP security guidelines',
      'Use security testing tools effectively',
      'Design secure APIs and web services',
      'Implement secure authentication flows',
      'Conduct security code reviews'
    ],
    modules: [
      {
        id: 'owasp-testing',
        title: 'OWASP Security Testing',
        description: 'Comprehensive security testing methodology based on OWASP guidelines',
        duration: '120 minutes',
        difficulty: 'intermediate',
        objectives: [
          'Master OWASP testing methodology',
          'Use automated security scanning tools',
          'Perform manual penetration testing',
          'Analyze and remediate vulnerabilities'
        ],
        content: `# OWASP Security Testing

## Testing Methodology

### Information Gathering
- Fingerprinting web servers and technologies
- Mapping application architecture
- Identifying entry points and attack surfaces

### Authentication Testing
- Testing for default credentials
- Brute force attack resistance
- Session management security
- Password policy enforcement

### Authorization Testing
- Path traversal vulnerabilities
- Privilege escalation attempts
- Insecure direct object references

## Security Testing Tools

### Automated Scanners
\`\`\`bash
# OWASP ZAP (Zed Attack Proxy)
docker run -t owasp/zap2docker-stable zap-baseline.py -t https://example.com

# Nikto web scanner
nikto -h https://example.com -ssl

# SQLMap for SQL injection testing
sqlmap -u "https://example.com/page?id=1" --dbs
\`\`\`

### Manual Testing Techniques
\`\`\`javascript
// XSS Testing Payloads
const xssPayloads = [
    '<script>alert("XSS")</script>',
    '"><script>alert("XSS")</script>',
    'javascript:alert("XSS")',
    '<img src=x onerror=alert("XSS")>'
];

// SQL Injection Testing
const sqlPayloads = [
    "' OR '1'='1",
    "'; DROP TABLE users; --",
    "' UNION SELECT 1,2,3 --"
];
\`\`\``,
        practice: {
          task: 'Conduct a comprehensive security assessment of a sample web application',
          starter: `// Security testing checklist
const securityTests = {
    authentication: [],
    authorization: [],
    inputValidation: [],
    sessionManagement: []
};

// Implement your testing strategy here`,
          hints: [
            'Test all input fields for injection vulnerabilities',
            'Verify authentication bypass attempts',
            'Check for insecure direct object references',
            'Test session management and token handling',
            'Validate SSL/TLS configuration'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'owasp-1',
              question: 'Which OWASP Top 10 vulnerability involves executing untrusted data as code?',
              options: ['Broken Authentication', 'Injection', 'XSS', 'Security Misconfiguration'],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'Injection flaws occur when untrusted data is sent to an interpreter as part of a command or query.',
              points: 15
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'security'
  }
];